import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { map, NEVER, Observable, takeUntil } from 'rxjs';
import { ConfirmDialogConfig, InputDialogConfig } from '../../dialog.types';
import { InputComponent } from '../../components/input/input.component';
import { ComponentType } from '@angular/cdk/portal';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private dialog = inject(MatDialog);

  confirm(config: ConfirmDialogConfig): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: config,
    });
    return dialogRef.afterClosed().pipe(map(value => !!value));
  }

  withConfirmation(
    config: ConfirmDialogConfig,
    until?: Observable<unknown>,
  ): (action: () => void) => void {
    return action => {
      const isConfirmationNeeded = !config.confirmIf || config.confirmIf();
      if (!isConfirmationNeeded) {
        action();
        return;
      }
      this.confirm(config)
        .pipe(
          map(confirmed => {
            if (confirmed) {
              action();
            }
          }),
          takeUntil(until ?? NEVER),
        )
        .subscribe();
    };
  }

  withDialogData<TConfig, TData>(
    component: ComponentType<unknown>,
    config: TConfig,
    until?: Observable<unknown>,
  ): (action: (data: TData) => void) => void {
    return action => {
      const dialogRef = this.dialog.open<unknown, TConfig, TData>(component, {
        data: config,
      });
      dialogRef
        .afterClosed()
        .pipe(
          map(data => {
            if (data !== undefined) {
              action(data);
            }
          }),
          takeUntil(until ?? NEVER),
        )
        .subscribe();
    };
  }

  input(config: InputDialogConfig): Observable<string | null> {
    const dialogRef = this.dialog.open(InputComponent, {
      data: config,
    });
    return dialogRef
      .afterClosed()
      .pipe(map(value => this.inputDialogValue(value)));
  }

  private inputDialogValue(afterClosedValue: unknown): string | null {
    // Should not happen
    if (!afterClosedValue) {
      return null;
    }
    // Should not happen
    if (
      typeof afterClosedValue !== 'object' ||
      !('cancel' in afterClosedValue)
    ) {
      return null;
    }
    // Cancellation
    if (afterClosedValue.cancel) {
      return null;
    }
    // Otherwise the value must be a string.  But we play safe and check the type.
    if (
      !('value' in afterClosedValue) ||
      typeof afterClosedValue.value !== 'string'
    ) {
      return '';
    }
    return afterClosedValue.value;
  }
}
